/**
 * Center Component
 *
 * A layout primitive for centering content both horizontally and vertically.
 * Replaces raw Tailwind like `flex items-center justify-center`.
 *
 * @example
 * ```tsx
 * import { Center } from '@/ui';
 *
 * <Center>
 *   <Spinner />
 * </Center>
 *
 * <Center fullScreen>
 *   <LoadingState />
 * </Center>
 * ```
 *
 * @see docs/ui-contract.md
 */

import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '@/shared/utils/utils';

// =============================================================================
// VARIANTS
// =============================================================================

const centerVariants = cva('flex items-center justify-center', {
  variants: {
    /**
     * Whether to use inline-flex instead of flex
     */
    inline: {
      true: 'inline-flex',
      false: 'flex',
    },
    /**
     * Whether the center should fill its container
     */
    fullWidth: {
      true: 'w-full',
      false: '',
    },
    /**
     * Whether the center should fill its container height
     */
    fullHeight: {
      true: 'h-full',
      false: '',
    },
    /**
     * Whether the center should fill the entire screen
     */
    fullScreen: {
      true: 'min-h-screen w-full',
      false: '',
    },
    /**
     * Gap between children (if multiple)
     */
    gap: {
      0: 'gap-0',
      1: 'gap-1',
      2: 'gap-2',
      3: 'gap-3',
      4: 'gap-4',
      5: 'gap-5',
      6: 'gap-6',
      8: 'gap-8',
      10: 'gap-10',
      12: 'gap-12',
    },
    /**
     * Direction of centered content
     */
    direction: {
      row: 'flex-row',
      column: 'flex-col',
    },
  },
  defaultVariants: {
    inline: false,
    fullWidth: false,
    fullHeight: false,
    fullScreen: false,
    gap: 0,
    direction: 'row',
  },
});

// =============================================================================
// TYPES
// =============================================================================

export interface CenterProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof centerVariants> {
  /**
   * The HTML element to render as
   * @default 'div'
   */
  as?: 'div' | 'section' | 'main' | 'span';
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Center - Centering layout primitive
 *
 * Use Center to center content horizontally and/or vertically.
 * This replaces patterns like:
 * - `className="flex items-center justify-center"`
 * - `className="min-h-screen flex items-center justify-center"`
 * - `className="h-full flex items-center justify-center"`
 */
export function Center({
  as: Component = 'div',
  inline,
  fullWidth,
  fullHeight,
  fullScreen,
  gap,
  direction,
  className,
  children,
  ...props
}: CenterProps): React.ReactElement {
  return (
    <Component
      className={cn(
        centerVariants({ inline, fullWidth, fullHeight, fullScreen, gap, direction }),
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

// =============================================================================
// CONVENIENCE COMPONENTS
// =============================================================================

/**
 * CenterStack - Centered content in a column
 */
export function CenterStack({
  children,
  gap = 4,
  className,
  ...props
}: Omit<CenterProps, 'direction'>): React.ReactElement {
  return (
    <Center direction="column" gap={gap} className={className} {...props}>
      {children}
    </Center>
  );
}

/**
 * FullScreenCenter - Full screen centered content (loading states, errors)
 */
export function FullScreenCenter({
  children,
  className,
  ...props
}: Omit<CenterProps, 'fullScreen'>): React.ReactElement {
  return (
    <Center fullScreen direction="column" gap={4} className={className} {...props}>
      {children}
    </Center>
  );
}

/**
 * AbsoluteCenter - Absolutely positioned centered content
 */
export function AbsoluteCenter({
  children,
  className,
  ...props
}: Omit<CenterProps, 'fullWidth' | 'fullHeight'>): React.ReactElement {
  return (
    <Center className={cn('absolute inset-0', className)} {...props}>
      {children}
    </Center>
  );
}
